
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import AddLinkIcon from '@mui/icons-material/AddLink';
import { toast } from 'react-toastify';
import { httpGet, httpDelete } from '../../HttpRequestHandling/httpRequestHandler';
import EnhancedDataGrid from '../Common/EnhancedDataGrid';
import Tooltip from '@mui/material/Tooltip';
import { useGridApiRef, GridActionsCellItem } from '@mui/x-data-grid';
import WifiTetheringOutlinedIcon from '@mui/icons-material/WifiTetheringOutlined';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { getFilter, setFilter } from '../Common/Utilities';
import { handleTestConnection } from './common';
import { isNewUi } from '../../redesign/uiMode';
import IntegrationCards from '../../redesign/admin/IntegrationCards';

export default function Integrations(props) {
    const navigate = useNavigate();
    const apiGridRef = useGridApiRef();

    const baseSessionPath = ['SystemSetup', 'Integrations'];
    const [sortModel, setSortModel] = useState(getFilter([...baseSessionPath, 'sortModel']) || []);

    const [rows, setRows] = React.useState([]);
    const [openDeleteConfirmation, setOpenDeleteConfirmation] = React.useState(false);
    const [deleteRowData, setDeleteRowData] = React.useState(null);


    const userInfo = JSON.parse(localStorage.userInfo);
    const companyCode = userInfo.companyCode;
    const modifyId = userInfo.id;

    const testConnection_onClick = async (rowData) => {
        const row = rowData.row;
        const payload = {
            adTechIntegrationId: row.adtechIntegrationId,
            shouldSave: true,
            integrationId: row.integrationId
        };
        
        const callback = (response) => {
            if (!!row.isActive !== response) {
                let newRows = rows.map((e) => {
                    if (e.adtechIntegrationId === row.adtechIntegrationId) {
                        e.isActive = response;
                    }
                    return e;
                })
                setRows([
                    ...newRows
                ]);
            }
            
        }
        const progress = props.inProgress;

        await handleTestConnection(payload, callback, progress);
    }


    const fetchIntegrations = useCallback(() => {
        if (!companyCode) {
            return;
        }
        props.inProgress?.(true);
        httpGet(`/api/integrations/${companyCode}`)
            .then((response) => {
                const data = Array.isArray(response.data) ? response.data : [];
             
                const withIds = data.map((e, i) => ({ ...e, rowId: e.adtechIntegrationId || i + 1 }));
                setRows(withIds);
                props.inProgress?.(false);
            })
            .catch((error) => {
                props.inProgress?.(false);
                toast.error('An error occurred while fetching Integrations');
          
            });
    }, [companyCode, props]);


    const callDelete = useCallback((cc, integrationId, mid) => {
        return httpDelete(`/api/integrations/${cc}/${integrationId}?modifyId=${encodeURIComponent(mid)}`);
    }, []);

    useEffect(() => {
        
        fetchIntegrations();
        
    }, []);

  
    const handleDeleteClick = React.useCallback((rowData) => {
        setDeleteRowData(rowData);
        setOpenDeleteConfirmation(true);
    }, []);

    const closeDeleteDialog = React.useCallback(() => {
        setOpenDeleteConfirmation(false);
        setDeleteRowData(null);
    }, []);

    const confirmDelete = useCallback(() => {
        if (!deleteRowData) return;
        props.inProgress?.(true);

        const integrationId = deleteRowData.adtechIntegrationId;
        const cc = deleteRowData.companyCode || companyCode;
        const mid = modifyId;

        setOpenDeleteConfirmation(false);
        setDeleteRowData(null);

        callDelete(cc, integrationId, mid)
            .then((resp) => {
                const ok = resp?.data === true;
                props.inProgress?.(false);
                if (ok) {
                    toast.success('Deleted Successfully');
                    fetchIntegrations();
                } else {
                    toast.error('Delete failed.');
                }
            })
            .catch((error) => {
                props.inProgress?.(false);
                toast.error('Delete failed.');
            });
    }, [deleteRowData, companyCode, modifyId, callDelete, fetchIntegrations, props]);


    


    const columns = [
        {
            headerName: 'Name',
            field: 'adtechIntegrationName',
            flex: 1,
            minWidth: 220
        },
        {
            headerName: 'Status',
            field: 'isActive',
            width: 160,
            sortable: true,
            renderCell: (params) => {
                
                const label = params.value || false;
                return (
                    <span
                        style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: 12,
                            fontSize: 12,
                            background: label ? 'rgba(16,185,129,.15)' : 'rgba(239,68,68,.15)',
                            color: label ? '#10b981' : '#ef4444',
                            fontWeight: 600
                        }}
                    >
                        {label ? 'Online' : 'Offline'}
                    </span>
                );
            }
        },
        {
            headerName: 'Type',
            field: 'integrationTypeName',
            width: 180
        },
        {
            headerName: 'Integration',
            field: 'integrationName',
            flex: 1,
            minWidth: 220
        },
        {
            headerName: 'Last updated',
            field: 'lastUpdated',
            width: 220,
            valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleString() : '')
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 150,
            getActions: (rowData) => [
                <GridActionsCellItem
                    key={`${rowData.id}-test`}
                    icon={
                        <Tooltip title={'Test Connection'}>
                            <WifiTetheringOutlinedIcon />
                        </Tooltip>
                    }
                    label="Test"
                    onClick={() => {
                        testConnection_onClick(rowData);
                    }}
                    color="inherit"
                />,
                <GridActionsCellItem
                    key={`${rowData.id}-edit`}
                    icon={
                        <Tooltip title={'Edit'}>
                            <EditIcon />
                        </Tooltip>
                    }
                    label="Edit"
                    onClick={() => {
                        navigate('/LinkIntegration', {
                            state: {
                                rowToBeUpdated: rowData.row
                                
                            }
                        });
                    }}
                    color="inherit"
                />,
                <GridActionsCellItem
                    key={`${rowData.id}-delete`}
                    icon={
                        <Tooltip title={'Delete'}>
                            <DeleteIcon />
                        </Tooltip>
                    }
                    label="Delete"
                    onClick={() => handleDeleteClick(rowData.row)}
                    color="inherit"
                />
            ]
        }
    ];

    return (
        <div>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h4" mt={3} mb={5}>
                    Integrations
                </Typography>
                <button
                    onClick={() => {
                        navigate('/LinkIntegration');
                    }}
                    style={{
                        backgroundColor: '#e0e0e0',
                        color: '#333',
                        padding: '6px 16px',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontWeight: 'bold',
                        transition: 'background 0.2s ease'
                    }}
                >   <AddLinkIcon />
                    Link Integration
                </button>
            </Box>

            <div className="form-group">
                {isNewUi() ? (
                    // New UI: one card per connection (redesign/admin/IntegrationCards.js), with the grid's own actions
                    <IntegrationCards rows={rows} sortModel={sortModel} onTest={testConnection_onClick} onDelete={handleDeleteClick}
                        onEdit={row => navigate('/LinkIntegration', { state: { rowToBeUpdated: row } })} />
                ) : (
                <div style={{ display: 'block', overflow: 'auto', width: '100%' }}>
                    <EnhancedDataGrid
                        rows={rows}
                        columns={columns}
                        nofilter={true}
                        apiRef={apiGridRef}
                        disableColumnMenu
                        pagination
                        pageSize={10}
                        disableSelectionOnClick
                        onSortModelChange={(model) => {
                            setSortModel(model);
                            setFilter([...baseSessionPath, 'sortModel'], model);
                        }}
                        sx={{

                            '& .MuiDataGrid-columnHeaderTitle': { fontSize: '13px' },
                            '& .MuiDataGrid-cell': { fontSize: '13px' },
                        }}

                        sortModel={sortModel}
                        getRowId={(row) => row.adtechIntegrationId ?? row.rowId}
                    />
                </div>
                )}
            </div>

            <Dialog open={openDeleteConfirmation} aria-labelledby="draggable-dialog-title">
                <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                    Delete Integration
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete integration "
                        {deleteRowData ? deleteRowData.integrationName || '' : ''}" ?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={confirmDelete} color="primary">
                        Yes
                    </Button>
                    <Button autoFocus onClick={closeDeleteDialog} color="primary">
                        No
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
